/**
 * v3.21-1699 — BankingAchPanel
 * ================================
 * Extends the v3.21-1695 canonical-auth wiring with the Manual BILL
 * Payment Fallback surface. When direct BILL ACH is available the
 * panel behaves exactly like v3.21-1695. When the backend reports
 * `manual_fallback_available=true` the panel surfaces a provider-
 * neutral banner + a guarded Reveal Banking For Manual Entry flow.
 *
 * Fallback bundle contract:
 *  · one password re-confirm reveals routing + account + type +
 *    holder + bank name for the SELECTED guest only
 *  · Copy Routing / Copy Account are only enabled while the session
 *    token is unexpired (auto-remask at 45s)
 *  · switching guest, switching client, closing the panel, or
 *    logout invalidates the reveal immediately
 *  · Copy actions are audited via /banking-ach/copy-audit — never
 *    log the routing/account value itself
 */
import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import api from "../lib/api";

const currency = (cents) =>
  `$${((Number(cents) || 0) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;

const REVEAL_TTL_SECONDS = 45;

const FALLBACK_REASON_COPY = {
  bill_credentials_missing:
    "Direct BILL ACH is unavailable — BILL credentials are not configured. You may use Manual BILL Payment Fallback.",
  bill_connectivity_unavailable:
    "Direct BILL ACH is unavailable — BILL cannot be reached right now. You may use Manual BILL Payment Fallback.",
  bill_capability_blocked:
    "Direct BILL ACH is unavailable — capability restriction is active. You may use Manual BILL Payment Fallback.",
  bank_encryption_key_missing:
    "Direct BILL ACH is unavailable — banking encryption key is not configured. Manual reveal is also disabled until this is corrected.",
};

export default function BankingAchPanel({ dealId, clientId, guest, guestLabel }) {
  const [status, setStatus] = useState(null);
  const [reveal, setReveal] = useState({ routing: "", account: "" });
  const [revealBusy, setRevealBusy] = useState(false);
  const [pw, setPw] = useState("");
  const [pwField, setPwField] = useState(null);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("phone");
  const [prep, setPrep] = useState(null);
  const [intent, setIntent] = useState(null);
  const [busy, setBusy] = useState(false);
  const remaskTimer = useRef(null);

  // v3.21-1699 — Manual BILL Fallback state
  const [fallbackPwOpen, setFallbackPwOpen] = useState(false);
  const [fallbackPw, setFallbackPw] = useState("");
  const [fallbackBusy, setFallbackBusy] = useState(false);
  const [fallbackBundle, setFallbackBundle] = useState(null); // {routing, account, account_type, account_holder, bank_name}
  const [fallbackSession, setFallbackSession] = useState(null); // {session_token, expires_at}
  const [fallbackRemaining, setFallbackRemaining] = useState(0);
  const fallbackTimerRef = useRef(null);
  const fallbackTickRef = useRef(null);

  const clearFallbackReveal = () => {
    if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
    if (fallbackTickRef.current) clearInterval(fallbackTickRef.current);
    fallbackTimerRef.current = null;
    fallbackTickRef.current = null;
    setFallbackBundle(null);
    setFallbackSession(null);
    setFallbackRemaining(0);
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/banking-ach/status");
        setStatus(data);
      } catch (_) { /* non-fatal */ }
    })();
    return () => {
      if (remaskTimer.current) clearTimeout(remaskTimer.current);
      clearFallbackReveal();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Guest change / client change immediately clears prior reveal.
  useEffect(() => {
    setReveal({ routing: "", account: "" });
    clearFallbackReveal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guest, clientId]);

  const scheduleRemask = (field) => {
    if (remaskTimer.current) clearTimeout(remaskTimer.current);
    remaskTimer.current = setTimeout(() => {
      setReveal((r) => ({ ...r, [field]: "" }));
    }, 30_000);
  };

  const openReveal = (field) => { setPwField(field); setPw(""); };

  const doReveal = async () => {
    if (!pwField) return;
    setRevealBusy(true);
    try {
      const { data } = await api.post("/banking-ach/reveal", {
        client_id: clientId, deal_id: dealId, guest, field: pwField, password: pw,
      });
      if (data?.ok) {
        setReveal((r) => ({ ...r, [pwField]: data.value }));
        scheduleRemask(pwField);
        toast.success(`Revealed — auto-hides in ${data.auto_remask_seconds || 30}s`);
        setPwField(null); setPw("");
      } else {
        toast.error("Reveal failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reveal denied");
    } finally { setRevealBusy(false); }
  };

  const authorize = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("Enter a positive ACH amount"); return;
    }
    setBusy(true);
    try {
      await api.post(`/banking-ach/deals/${dealId}/authorize`, {
        guest, amount: parseFloat(amount), method,
        language_version: "v1", acknowledgement: true,
      });
      toast.success("ACH authorization captured");
      const { data: p } = await api.post(`/banking-ach/deals/${dealId}/prepare`, {
        guest, amount: parseFloat(amount),
      });
      setPrep(p);
      if (!p.ok) toast.error(p.blocked_reason || p.error);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Authorization failed");
    } finally { setBusy(false); }
  };

  const doProcess = async () => {
    if (!amount) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/banking-ach/deals/${dealId}/process`, {
        guest, amount: parseFloat(amount),
      });
      if (data.ok) {
        toast.success(`ACH submitted — intent ${data.intent_id?.slice(0, 8)}`);
        setIntent({ id: data.intent_id, status: data.status });
      } else {
        toast.error(data.blocked_reason || data.error || "Process ACH blocked");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Process ACH failed");
    } finally { setBusy(false); }
  };

  const poll = async () => {
    if (!intent?.id) return;
    try {
      const { data } = await api.get(`/banking-ach/intents/${intent.id}`);
      setIntent(data);
    } catch (_) { /* ignore */ }
  };

  const openHostedLink = async () => {
    try {
      const { data } = await api.post(`/banking-ach/deals/${dealId}/hosted-payment-link`, {});
      if (data?.ok && data.payment_url) {
        window.open(data.payment_url, "_blank", "noopener,noreferrer");
        toast.success("BILL hosted payment link opened");
      } else {
        toast.error(data?.blocked_reason || data?.error || "Hosted link unavailable");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hosted link failed");
    }
  };

  // v3.21-1699 — Manual BILL fallback handlers
  const openFallbackPw = () => {
    clearFallbackReveal();
    setFallbackPw("");
    setFallbackPwOpen(true);
  };

  const doFallbackReveal = async () => {
    if (!fallbackPw) return;
    setFallbackBusy(true);
    try {
      const { data } = await api.post("/banking-ach/reveal-bundle", {
        client_id: clientId, deal_id: dealId, guest, password: fallbackPw,
      });
      if (!data?.ok) { toast.error("Reveal failed"); return; }
      setFallbackBundle(data.bundle);
      setFallbackSession({
        session_token: data.session_token,
        expires_at: data.expires_at,
      });
      const seconds = Number(data.auto_remask_seconds || REVEAL_TTL_SECONDS);
      setFallbackRemaining(seconds);
      fallbackTimerRef.current = setTimeout(() => {
        clearFallbackReveal();
      }, seconds * 1000);
      fallbackTickRef.current = setInterval(() => {
        setFallbackRemaining((s) => (s > 0 ? s - 1 : 0));
      }, 1000);
      setFallbackPwOpen(false);
      setFallbackPw("");
      toast.success(`Banking revealed — auto-hides in ${seconds}s`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reveal denied");
    } finally {
      setFallbackBusy(false);
    }
  };

  const cancelFallbackPw = () => {
    setFallbackPwOpen(false);
    setFallbackPw("");
  };

  const doCopy = async (kind /* 'routing' | 'account' */) => {
    if (!fallbackBundle || !fallbackSession) return;
    const value = kind === "routing" ? fallbackBundle.routing : fallbackBundle.account;
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
    } catch (_) {
      toast.error("Clipboard unavailable in this browser");
      return;
    }
    try {
      await api.post("/banking-ach/copy-audit", {
        session_token: fallbackSession.session_token,
        client_id: clientId,
        deal_id: dealId,
        guest,
        action: kind === "routing" ? "routing_copied" : "account_copied",
      });
    } catch (e) {
      // Audit failure should not surface the value; just warn.
      toast.error(e?.response?.data?.detail || "Copy audit failed");
    }
    toast.success(`${kind === "routing" ? "Routing" : "Account"} copied to clipboard`);
  };

  const canProcess =
    status?.bill_writable === true &&
    status?.bank_encryption_key_configured === true &&
    prep?.ok === true;

  const blockedReason = !status?.bill_writable
    ? status?.bill_blocked_reason
    : !status?.bank_encryption_key_configured
      ? "BANK_ENCRYPTION_KEY is not configured — banking updates disabled."
      : prep && !prep.ok
        ? prep.blocked_reason
        : null;

  const showManualFallback = status?.manual_fallback_available === true;
  const fallbackReasonCopy =
    FALLBACK_REASON_COPY[status?.manual_fallback_reason] ||
    "Direct BILL ACH is unavailable. You may use Manual BILL Payment Fallback.";
  const revealAllowed = status?.bank_encryption_key_configured === true;

  return (
    <div
      className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-2 space-y-2"
      data-testid={`banking-ach-panel-${guest}`}
    >
      <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
        {guestLabel} · Process ACH
      </div>
      <div className="flex items-center gap-2 text-[11px]">
        <button type="button"
          className="rounded border border-slate-300 bg-white px-2 py-1 text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          onClick={() => openReveal("routing")}
          disabled={!status?.bank_encryption_key_configured}
          data-testid={`reveal-routing-${guest}`}>Reveal Routing</button>
        <button type="button"
          className="rounded border border-slate-300 bg-white px-2 py-1 text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          onClick={() => openReveal("account")}
          disabled={!status?.bank_encryption_key_configured}
          data-testid={`reveal-account-${guest}`}>Reveal Account</button>
        {reveal.routing && (
          <span className="rounded bg-yellow-100 px-1 py-0.5 text-[10px] text-yellow-900" data-testid={`revealed-routing-${guest}`}>RTN: {reveal.routing}</span>
        )}
        {reveal.account && (
          <span className="rounded bg-yellow-100 px-1 py-0.5 text-[10px] text-yellow-900" data-testid={`revealed-account-${guest}`}>ACCT: {reveal.account}</span>
        )}
      </div>

      {pwField && (
        <div className="rounded border border-yellow-300 bg-yellow-50 p-2 space-y-1" data-testid={`reveal-password-modal-${guest}`}>
          <div className="text-[11px] text-yellow-900">Confirm your password to reveal the {pwField} number. This reveal will be audited.</div>
          <input type="password" value={pw} onChange={(e) => setPw(e.target.value)}
            className="w-full rounded border border-slate-300 px-2 py-1 text-xs" placeholder="Password"
            data-testid={`reveal-password-input-${guest}`} />
          <div className="flex gap-2">
            <button type="button" onClick={doReveal} disabled={revealBusy || !pw}
              className="rounded bg-slate-800 px-2 py-1 text-[11px] text-white disabled:opacity-40"
              data-testid={`reveal-confirm-${guest}`}>Confirm &amp; Reveal</button>
            <button type="button" onClick={() => { setPwField(null); setPw(""); }}
              className="rounded border border-slate-300 bg-white px-2 py-1 text-[11px]"
              data-testid={`reveal-cancel-${guest}`}>Cancel</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
        <label className="flex flex-col">
          <span className="text-slate-500">ACH Amount</span>
          <input type="number" step="0.01" min="0" value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="rounded border border-slate-300 px-2 py-1" placeholder="0.00"
            data-testid={`ach-amount-${guest}`} />
        </label>
        <label className="flex flex-col">
          <span className="text-slate-500">Authorization Method</span>
          <select value={method} onChange={(e) => setMethod(e.target.value)}
            className="rounded border border-slate-300 px-2 py-1"
            data-testid={`ach-method-${guest}`}>
            <option value="phone">Phone</option>
            <option value="signed_form">Signed Form</option>
            <option value="online">Online</option>
            <option value="in_person">In Person</option>
          </select>
        </label>
      </div>

      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={authorize} disabled={busy || !amount}
          className="rounded border border-slate-400 bg-white px-2 py-1 text-[11px] hover:bg-slate-100 disabled:opacity-40"
          data-testid={`ach-authorize-${guest}`}>Capture Authorization</button>
        <button type="button" onClick={doProcess} disabled={busy || !canProcess}
          className="rounded bg-emerald-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-40"
          data-testid={`process-ach-${guest}`}>PROCESS ACH</button>
        {intent?.id && (
          <button type="button" onClick={poll}
            className="rounded border border-slate-300 bg-white px-2 py-1 text-[11px]"
            data-testid={`ach-poll-${guest}`}>Refresh Status</button>
        )}
        <button type="button" onClick={openHostedLink}
          className="rounded border border-slate-400 bg-white px-2 py-1 text-[11px] hover:bg-slate-100"
          data-testid={`hosted-link-${guest}`}>Send BILL Payment Link</button>
      </div>

      {prep && (
        <div className="text-[10px] text-slate-600" data-testid={`ach-prep-${guest}`}>
          {prep.ok ? (
            <>Cap {currency(prep.cap_cents)} · Hub {currency(prep.hub_outstanding_cents)}
              {prep.bill_outstanding_cents !== null && (<> · BILL {currency(prep.bill_outstanding_cents)}</>)}
            </>
          ) : (<span className="text-rose-600">Blocked: {prep.blocked_reason || prep.error}</span>)}
        </div>
      )}

      {intent && (
        <div className="rounded bg-white border border-slate-200 p-2 text-[10px] space-y-0.5"
          data-testid={`ach-intent-${guest}`}>
          <div><b>Status:</b> {intent.status}</div>
          <div><b>Amount:</b> {currency(intent.amount_cents)}</div>
          {intent.bill_received_payment_id && (<div><b>BILL Ref:</b> {intent.bill_received_payment_id}</div>)}
          {intent.error && <div className="text-rose-600"><b>Error:</b> {intent.error}</div>}
          <div><b>Submitted:</b> {intent.created_at}</div>
          {intent.updated_at && intent.updated_at !== intent.created_at && (<div><b>Updated:</b> {intent.updated_at}</div>)}
        </div>
      )}

      {/* v3.21-1699 — Manual BILL Payment Fallback panel */}
      {showManualFallback && (
        <div
          className="rounded-md border border-amber-300 bg-amber-50 p-2 space-y-2"
          data-testid={`manual-bill-fallback-${guest}`}
        >
          <div className="text-[11px] font-semibold text-amber-900 uppercase tracking-wide">
            Manual BILL Payment Fallback
          </div>
          <div className="text-[11px] text-amber-900" data-testid={`manual-bill-fallback-banner-${guest}`}>
            {fallbackReasonCopy}
          </div>

          <div className="flex flex-wrap gap-2">
            <button type="button"
              onClick={openFallbackPw}
              disabled={!revealAllowed}
              className="rounded border border-amber-500 bg-white px-2 py-1 text-[11px] text-amber-900 hover:bg-amber-100 disabled:opacity-40"
              data-testid={`manual-reveal-open-${guest}`}>Reveal Banking for Manual Entry</button>
            <button type="button"
              onClick={openHostedLink}
              className="rounded border border-amber-500 bg-white px-2 py-1 text-[11px] text-amber-900 hover:bg-amber-100"
              data-testid={`manual-hosted-link-${guest}`}>Send BILL Payment Link</button>
          </div>

          {fallbackPwOpen && (
            <div className="rounded border border-amber-400 bg-white p-2 space-y-1" data-testid={`manual-reveal-pw-${guest}`}>
              <div className="text-[11px] text-amber-900">
                Confirm your password to reveal full banking for {guestLabel}. This reveal will be audited and auto-hide after {REVEAL_TTL_SECONDS} seconds.
              </div>
              <input type="password" value={fallbackPw} onChange={(e) => setFallbackPw(e.target.value)}
                className="w-full rounded border border-slate-300 px-2 py-1 text-xs" placeholder="Password"
                data-testid={`manual-reveal-pw-input-${guest}`} />
              <div className="flex gap-2">
                <button type="button" onClick={doFallbackReveal} disabled={fallbackBusy || !fallbackPw}
                  className="rounded bg-amber-700 px-2 py-1 text-[11px] text-white disabled:opacity-40"
                  data-testid={`manual-reveal-confirm-${guest}`}>Confirm &amp; Reveal</button>
                <button type="button" onClick={cancelFallbackPw}
                  className="rounded border border-slate-300 bg-white px-2 py-1 text-[11px]"
                  data-testid={`manual-reveal-cancel-${guest}`}>Cancel</button>
              </div>
            </div>
          )}

          {fallbackBundle && (
            <div
              className="rounded border border-amber-400 bg-white p-2 space-y-1 text-[11px] text-slate-800"
              data-testid={`manual-reveal-bundle-${guest}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-semibold text-amber-900">
                  {guestLabel} — full banking (auto-hides in {fallbackRemaining}s)
                </span>
                <button type="button" onClick={clearFallbackReveal}
                  className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[10px] hover:bg-slate-100"
                  data-testid={`manual-reveal-close-${guest}`}>Hide</button>
              </div>
              <div><span className="text-slate-500">Routing:</span> <span className="font-mono" data-testid={`manual-routing-${guest}`}>{fallbackBundle.routing}</span></div>
              <div><span className="text-slate-500">Account:</span> <span className="font-mono" data-testid={`manual-account-${guest}`}>{fallbackBundle.account}</span></div>
              <div><span className="text-slate-500">Account Type:</span> <span data-testid={`manual-account-type-${guest}`}>{fallbackBundle.account_type || "—"}</span></div>
              <div><span className="text-slate-500">Account Holder:</span> <span data-testid={`manual-account-holder-${guest}`}>{fallbackBundle.account_holder || "—"}</span></div>
              <div><span className="text-slate-500">Bank Name:</span> <span data-testid={`manual-bank-name-${guest}`}>{fallbackBundle.bank_name || "—"}</span></div>
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={() => doCopy("routing")}
                  className="rounded border border-slate-400 bg-white px-2 py-1 text-[11px] hover:bg-slate-100"
                  data-testid={`manual-copy-routing-${guest}`}>Copy Routing</button>
                <button type="button" onClick={() => doCopy("account")}
                  className="rounded border border-slate-400 bg-white px-2 py-1 text-[11px] hover:bg-slate-100"
                  data-testid={`manual-copy-account-${guest}`}>Copy Account</button>
              </div>
            </div>
          )}
        </div>
      )}

      {blockedReason && !prep?.ok && !showManualFallback && (
        <div className="text-[10px] text-rose-700 bg-rose-50 border border-rose-200 rounded p-1" data-testid={`ach-blocked-${guest}`}>
          {blockedReason}
        </div>
      )}
    </div>
  );
}
